import {
    SAILS_BNT_STORAGE,
    SAILS_PARAM_TAUSCHBONUS,
    SAILS_PARAM_TRADE_IN,
    TRADE_IN_DEVICE_STORAGE,
} from '@vfde-fe/sails-constants';
import { updateStorageSaga } from '@vfde-fe/sails-storage';
import {
    SelectorReturnType,
    getSessionStorageItemJson,
    request,
    setSessionStorageItemJson,
} from '@vfde-fe/sails_core';
import { getApiCredentials } from '@vfde-fe/utils';
import {
    StrictEffect,
    all,
    call,
    put,
    select,
    takeLatest,
} from 'redux-saga/effects';
import {
    API_CREDENTIALS,
    CREATE_INITIAL_STATE,
    DELETE_SELECTED_TRADE_IN_DEVICE,
    GET_TRADE_IN_DEVICES,
    MAX_SUGGEST_FIELD_INPUT_LENGTH,
    MIN_SUGGEST_FIELD_INPUT_LENGTH,
    SET_TRADE_IN_DEVICE,
    TAUSCHBONUS_PROMO_TILE,
    TOGGLE_TRADE_IN_SWITCH,
} from './constants';
import { filterAndFormatDevices } from './helpers/filterAndFormatDevices';
import { getTradeIn } from './helpers/getCustomerConfig';
import { getDeviceWithId } from './helpers/getDeviceWithId';
import {
    ApiDevice,
    ApiResponse,
    Device,
} from './interfaces/api';
import {
    selectSuggestFieldValue,
    selectDevices,
    selectSelectedDeviceId,
    selectTradeInSwitch,
    selectTradeIn,
} from './selectors';
import { sanitizeInput } from './helpers/sanitizeInput';
import { TOGGLE_HARDWARE_ONLY } from '../DeviceDetail/constants';
import { selectHardwareOnly } from '../DeviceDetail/selectors';
import { trackTradeIn, trackTradeInActivated, trackTradeInDeactivated } from 'Helper/tracking';
import { getIsTauschbonusEligible } from '../DeviceDetail/helpers/getIsTauschbonusEligible';
import { CLASSNAME_HIDDEN } from '@vfde-brix/core';
import { tradeInActionCreators } from './actions';

/**
 * Tariff saga
 * Watches actions
 */
export default function* tradeInSaga (): Generator<StrictEffect> {
    yield takeLatest(CREATE_INITIAL_STATE, createInitialStateSaga);
    yield takeLatest(GET_TRADE_IN_DEVICES, getTradeInDevicesSaga);
    yield takeLatest(TOGGLE_TRADE_IN_SWITCH, toggleTradeInSwitchSaga);
    yield takeLatest(SET_TRADE_IN_DEVICE, selectTradeInDeviceSaga);
    yield takeLatest(DELETE_SELECTED_TRADE_IN_DEVICE, deleteSelectedTradeInDeviceSaga);
    yield takeLatest(TOGGLE_HARDWARE_ONLY, toggleHardwareOnlySaga);
}

/**
 * createInitialState
 */
export function* createInitialStateSaga (): Generator<StrictEffect> {
    const storedDevice = (yield call(getSessionStorageItemJson, TRADE_IN_DEVICE_STORAGE)) as Device | null;
    const isTradeIn = getTradeIn();

    if (storedDevice && storedDevice.id) {
        yield put(tradeInActionCreators.setSelectedTradeInDeviceId(storedDevice.id));
    }

    yield put(tradeInActionCreators.setDefaultState(isTradeIn));
}

/**
 * getDevices saga
 * Fetches the API data
 */
export function* getTradeInDevicesSaga (): Generator<StrictEffect> {
    const inputValue = (yield select(selectSuggestFieldValue())) as SelectorReturnType<typeof selectSuggestFieldValue>;
    const sanitizedInputValue = (yield call(sanitizeInput, inputValue)) as ReturnType<typeof sanitizeInput>;

    if (sanitizedInputValue.length < MIN_SUGGEST_FIELD_INPUT_LENGTH) {
        // search input is too short, treat it as default case where payload is null
        yield put(tradeInActionCreators.getTradeInDevicesSuccess(null));

        return;
    } else if (sanitizedInputValue.length > MAX_SUGGEST_FIELD_INPUT_LENGTH) {
        // search input is too long, treat it as device not found
        yield put(tradeInActionCreators.getTradeInDevicesSuccess([]));

        return;
    }

    try {
        const { key, url } = getApiCredentials(API_CREDENTIALS);
        const requestUrl = `${url}${encodeURIComponent(sanitizedInputValue)}`;
        const options = {
            method: 'GET',
            headers: {
                'Authorization': `Basic ${key}`,
                'Content-Type': 'application/json',
                'vf-country-code': 'DE',
                'vf-project': 'DLS',
            },
        };

        const response = (yield call(request, requestUrl, options)) as ApiResponse<ApiDevice[]> ;

        if (!response || response.status !== 200) {
            yield put(tradeInActionCreators.getTradeInDevicesFailed());

            return;
        }

        const { body: apiDevices } = response;
        const devices = (yield call(filterAndFormatDevices, apiDevices)) as ReturnType<typeof filterAndFormatDevices>;

        yield put(tradeInActionCreators.getTradeInDevicesSuccess(devices));
    } catch (e) {
        yield put(tradeInActionCreators.getTradeInDevicesFailed());
    }
}

/**
 * select device saga
 */
export function* selectTradeInDeviceSaga (): Generator<StrictEffect> {
    const devices = (yield select(selectDevices())) as SelectorReturnType<typeof selectDevices>;
    const selectedDeviceId = (yield select(selectSelectedDeviceId())) as SelectorReturnType<typeof selectSelectedDeviceId>;
    const selectedDevice = (yield call(getDeviceWithId, devices, selectedDeviceId!)) as ReturnType<typeof getDeviceWithId>;

    if (!selectedDevice) {
        return;
    }

    yield call(setSessionStorageItemJson, TRADE_IN_DEVICE_STORAGE, selectedDevice);
    yield call(updateStorageSaga, SAILS_BNT_STORAGE, {
        [SAILS_PARAM_TRADE_IN]: !!selectedDeviceId,
        [SAILS_PARAM_TAUSCHBONUS]: getIsTauschbonusEligible() ? !!selectedDeviceId : false,
    });
    yield call(trackTradeIn, true, selectedDevice);
}

/**
 * toggle hardware only
 */
export function* toggleHardwareOnlySaga (): Generator<StrictEffect> {
    const hardwareOnly = (yield select(selectHardwareOnly())) as SelectorReturnType<typeof selectHardwareOnly>;
    const tradeInSwitch = (yield select(selectTradeInSwitch())) as SelectorReturnType<typeof selectTradeInSwitch>;
    const isTradeIn = (yield call(getTradeIn)) as ReturnType<typeof getTradeIn>;
    const tauschbonusPromoTile = document.getElementById(TAUSCHBONUS_PROMO_TILE);

    if (hardwareOnly) {

        if (tradeInSwitch) {
            yield put(tradeInActionCreators.toggleTradeInSwitch());
        }
    } else {

        if (isTradeIn) {
            yield put(tradeInActionCreators.toggleTradeInSwitch());
        }
    }
    tauschbonusPromoTile && tauschbonusPromoTile.classList.toggle(CLASSNAME_HIDDEN, hardwareOnly);
}

/**
 * Toggle Trade-in saga
 */
export function* toggleTradeInSwitchSaga (): Generator<StrictEffect> {
    const [
        tradeInSwitch,
        isTradeIn,
        selectedDeviceId,
        selectedDevice,
    ] = (yield all([
        select(selectTradeInSwitch()),
        select(selectTradeIn()),
        select(selectSelectedDeviceId()),
        call(getSessionStorageItemJson, TRADE_IN_DEVICE_STORAGE),
    ])) as [
        SelectorReturnType<typeof selectTradeInSwitch>,
        SelectorReturnType<typeof selectTradeIn>,
        SelectorReturnType<typeof selectSelectedDeviceId>,
        Device | null,
    ];

    yield call(updateStorageSaga, SAILS_BNT_STORAGE, {
        [SAILS_PARAM_TRADE_IN]: isTradeIn,
        [SAILS_PARAM_TAUSCHBONUS]: isTradeIn ? getIsTauschbonusEligible() : false,
    });

    if (tradeInSwitch) {
        // trade-in switch was activated
        yield call(trackTradeInActivated, selectedDeviceId, selectedDevice);
    } else {
        // trade-in switch was deactivated,
        // reset the devices payload to the default value
        // so that the 'device not found' / error notification will disappear
        yield put(tradeInActionCreators.getTradeInDevicesSuccess(null));
        yield call(trackTradeInDeactivated, selectedDeviceId, selectedDevice);
    }
}

/**
 * delete selected device saga
 * @TODO: this is triggered twice on device deletion. Needs to be fixed.
 */
export function* deleteSelectedTradeInDeviceSaga (): Generator<StrictEffect> {
    const selectedDevice = (yield call(getSessionStorageItemJson, TRADE_IN_DEVICE_STORAGE)) as Device | null;
    yield put(tradeInActionCreators.setSelectedTradeInDeviceId(''));
    yield call([window.sessionStorage, 'removeItem'], TRADE_IN_DEVICE_STORAGE);
    yield call(updateStorageSaga, SAILS_BNT_STORAGE, {
        [SAILS_PARAM_TRADE_IN]: false,
    });
    yield call(trackTradeIn, false, selectedDevice);
}
